/* eslint-disable camelcase */
import { FlowSpec } from '@digibee/flow';

import { Nullish } from '~/common/types/Nullish';
import { Schema } from '~/entities/Capsule';
import { OutSpec } from '~/entities/OutSpec';
import { TriggerSpec } from '~/entities/TriggerSpec';
import type { AIMessageData } from '~/scenes/AI/components/AIChat/AIChat.machine';

export type Insights = {
  type: string;
  message: string;
  details: string;
  connectorId: string;
};

export type AIMessage = {
  actions?: Record<string, unknown>[];
  triggers?: Record<string, unknown>[];
  data?: AIMessageData;
  sender: string;
  feedback?: 'like' | 'dislike';
  text: string;
  off_topic?: boolean;
};

export type SendChatMessageParams2 = {
  realm: string;
  target: string;
  lang: string;
  history?: AIMessage[];
  message: AIMessage;
  token: string;
};

export type ChatHistoryParams = {
  realm: string;
  user: string;
  context: string;
  pipeline: string;
  isGroupedByConversation: boolean;
  token: string;
};

export type UiComponentType = {
  name: string;
  type?: string;
  label?: string;
  value: string;
  editable: boolean;
  translations?: {
    pt_br: string;
    en: string;
  } | null;
};

export type ActionsType = {
  name: string;
  type: string;
  label: string;
  value: string;
  related_ui_component: string;
};

export type ContextType = {
  flowSpec: Nullish<FlowSpec>;
  flowName: string;
  triggerSpec?: Nullish<TriggerSpec>;
  id?: string;
  title?: string;
  description?: Nullish<string>;
  outSpec?: Nullish<OutSpec>;
  configExample?:
    | {
        type: string;
        name: string;
        capsuleCollection: string;
        capsuleCollectionVersion: number;
        capsule: string;
        capsuleVersionMajor: number;
        capsuleVersionMinor: number;
        stepName: string;
        params: {
          url: string;
        };
      }
    | null
    | undefined;
  jsonSchema?: Schema[];
};

export type SingleChatMessage = {
  realm: string;
  user: string;
  context: string;
  pipeline: string;
  sender: string;
  state_id: string;
  created_at: string;
  ui_components: UiComponentType[];
  actions: ActionsType[];
};

export type ChatMessage = {
  state_id: string;
  messages: SingleChatMessage[];
};

export type ChatHistoryResponse = {
  chat_history: ChatMessage[];
};

export type ChatResumeParams = {
  realm: string;
  user: string;
  context: string;
  content?: string;
  pipeline: string;
  token: string;
  actionResult?: string;
  autoNextState?: string;
  uiComponents?: UiComponentType[];
  contextInfo?: ContextType;
};

export type ChatResumeResponse = {
  state: string;
  ui_components: UiComponentType[];
  actions: ActionsType[];
  auto_next_state: string;
  internal_auto_next_state: string;
  invoke: Record<string, unknown> | null;
  state_id: string;
  realm: string;
  user: string;
  context: string;
  pipeline: string;
  formatted_user_message: {
    ui_components: UiComponentType[];
    actions: ActionsType[];
  };
  action_result?: string;
  body: ChatResumeParams;
};

export const PROMPT_STRATEGIST_INTENT_MAX_LENGTH = 1000;

export function clampPromptStrategistIntent(intent: string): string {
  const trimmed = intent.trim();
  if (trimmed.length <= PROMPT_STRATEGIST_INTENT_MAX_LENGTH) {
    return trimmed;
  }
  return trimmed.slice(0, PROMPT_STRATEGIST_INTENT_MAX_LENGTH);
}

/**
 * POST .../prompt-strategist/system-prompt — `intent` + `provider` required; optional context for coordinator meta prompts.
 */
export type PromptStrategistSystemPromptParams = {
  realm: string;
  provider: string;
  /** Trimmed and clamped to {@link PROMPT_STRATEGIST_INTENT_MAX_LENGTH} before POST. */
  intent: string;
  initialSystemPrompt?: string;
  /** Serialized tools definition (e.g. JSON); embedded verbatim when non-blank. */
  tools?: string;
  /** Serialized files list (e.g. JSON); embedded verbatim when non-blank. */
  files?: string;
  /** Serialized structured output / guardrails context; embedded verbatim when non-blank. */
  structuredOutput?: string;
  /** When aborted, Axios rejects with a cancel error (popover close / new generate). */
  signal?: AbortSignal;
};

export type PromptStrategistSystemPromptResponse = {
  systemPrompt: string;
  provider: string;
};

export type PromptStrategistUserPromptParams = {
  realm: string;
  provider: string;
  intent: string;
  /** Required by backend; aligns generated user prompt with current system message. */
  currentSystemPrompt: string;
  initialUserPrompt?: string;
  signal?: AbortSignal;
};

export type PromptStrategistUserPromptResponse = {
  userPrompt: string;
  provider?: string;
};

export type PromptStrategistJsonSchemaParams = {
  realm: string;
  intent: string;
  /** Omit or blank for CREATE mode; set for IMPROVE mode. Not escaped server-side. */
  currentSchema?: string;
  initialSystemPrompt?: string;
  initialUserPrompt?: string;
  signal?: AbortSignal;
};

export type PromptStrategistJsonSchemaResponse = {
  jsonSchema: string;
};

export type PromptStrategistMcpToolNameDescriptionParams = {
  realm: string;
  intent: string;
  toolIdOrName: string;
  currentName?: string;
  currentDescription?: string;
  flowSpec?: string;
  signal?: AbortSignal;
};

export type PromptStrategistMcpToolNameDescriptionResponse = {
  name: string;
  description: string;
};

export type PromptStrategistMcpServerDescriptionParams = {
  realm: string;
  intent: string;
  currentName?: string;
  currentDescription?: string;
  triggerSpec?: string;
  signal?: AbortSignal;
};

export type PromptStrategistMcpServerDescriptionResponse = {
  description: string;
};
